import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { httpsCallable } from 'firebase/functions';
import { useSearchParams } from 'react-router-dom';
import { functions } from '../firebase';
import { StatusPill, UI, textButtonSx } from '../components/whatsapp/settingsUi';
import type { PillTone } from '../components/whatsapp/settingsUi';
import { useTemplateStatuses } from '../components/whatsapp/hubTemplates';
import HubAutomationsTab from '../components/whatsapp/HubAutomationsTab';
import HubTemplatesTab from '../components/whatsapp/HubTemplatesTab';
import HubContactsTab from '../components/whatsapp/HubContactsTab';
import WorkingHoursSection from '../components/whatsapp/WorkingHoursSection';
import WhatsappStudioTab from '../components/whatsapp/WhatsappStudioTab';

const TABS = [
  { key: 'customer-flow', label: 'Customer Flow' },
  { key: 'conversations', label: 'Conversations · Free' },
  { key: 'journeys', label: 'Journeys · Paid' },
  { key: 'templates', label: 'Templates' },
  { key: 'studio', label: 'Studio' },
  { key: 'contacts', label: 'Contacts & opt-in' },
  { key: 'settings', label: 'Settings' },
];

interface Health {
  ok: boolean;
  metaError: string | null;
  displayPhoneNumber: string;
  verifiedName: string;
  qualityRating: string;
  messagingLimitTier: string;
  lastDeliveredAt: string | null;
  lastFailedAt: string | null;
  lastFailedError: string | null;
  checkedAt: string;
}

const QUALITY: Record<string, { label: string; tone: PillTone }> = {
  GREEN: { label: 'Quality high', tone: 'green' },
  YELLOW: { label: 'Quality medium', tone: 'amber' },
  RED: { label: 'Quality low', tone: 'slate' },
};

const TIER_LABEL: Record<string, string> = {
  TIER_50: '50/day',
  TIER_250: '250/day',
  TIER_1K: '1K/day',
  TIER_10K: '10K/day',
  TIER_100K: '100K/day',
  TIER_UNLIMITED: 'Unlimited',
};

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }) : '';

const HealthBanner = ({ health, loading, error, onCheck }: { health: Health | null; loading: boolean; error: string | null; onCheck: () => void }) => {
  const failedAfterDelivered =
    !!health?.lastFailedAt && (!health.lastDeliveredAt || health.lastFailedAt > health.lastDeliveredAt);
  const bad = !!error || !!health?.metaError || failedAfterDelivered;
  const text = error ?
    `Could not check WhatsApp: ${error}` :
    !health ?
      'Checking WhatsApp…' :
      health.metaError ?
        `Meta says: ${health.metaError}` :
        failedAfterDelivered ?
          `Last send failed ${when(health.lastFailedAt)}: ${health.lastFailedError || 'unknown error'}.` :
          `Sending works.${health.lastDeliveredAt ? ` Last message delivered ${when(health.lastDeliveredAt)}.` : ''} Checked with Meta ${when(health.checkedAt)}.`;
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      sx={{
        px: 1.75,
        py: 1,
        borderRadius: 2,
        bgcolor: bad ? '#fbeceb' : '#edf6ee',
        border: `1px solid ${bad ? '#f0cfcb' : '#cfe6d2'}`,
      }}
    >
      {bad ? <ErrorOutlineIcon sx={{ fontSize: 18, color: '#a12a22' }} /> : <CheckCircleOutlineIcon sx={{ fontSize: 18, color: '#2f6b35' }} />}
      <Typography sx={{ fontSize: 13, color: bad ? '#a12a22' : '#2f6b35', flex: 1 }}>{text}</Typography>
      <Button size="small" variant="outlined" disabled={loading} onClick={onCheck} sx={{ ...textButtonSx, bgcolor: UI.surface }}>
        {loading ? 'Checking…' : 'Check now'}
      </Button>
    </Stack>
  );
};

const WhatsappHubPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') === 'automations' ? 'conversations' : searchParams.get('tab');
  const tab = TABS.some((t) => t.key === rawTab) ? String(rawTab) : 'conversations';
  const templates = useTemplateStatuses();
  const [health, setHealth] = useState<Health | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState<string | null>(null);

  const checkHealth = useCallback(async () => {
    setHealthLoading(true);
    setHealthError(null);
    try {
      const res: any = await httpsCallable(functions, 'whatsappHubHealth')({});
      setHealth(res.data as Health);
    } catch (err: any) {
      setHealthError(String(err?.message || err));
    } finally {
      setHealthLoading(false);
    }
  }, []);

  useEffect(() => {
    checkHealth();
  }, [checkHealth]);

  const quality = health ? QUALITY[health.qualityRating] : null;

  return (
    <Box sx={{ fontFamily: UI.body, height: '100%', overflowY: 'auto', bgcolor: UI.canvas }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 2, maxWidth: 1560 }}>
        <Stack direction="row" alignItems="baseline" gap={1.25} flexWrap="wrap" sx={{ mb: 0.5 }}>
          <Typography sx={{ fontFamily: UI.heading, fontSize: 21, fontWeight: 500, color: UI.ink }}>WhatsApp</Typography>
          {health?.displayPhoneNumber && (
            <Typography sx={{ fontSize: 12.5, color: UI.muted }}>
              {health.displayPhoneNumber}
              {health.verifiedName ? ` · ${health.verifiedName}` : ''}
              {health.messagingLimitTier ? ` · limit ${TIER_LABEL[health.messagingLimitTier] || health.messagingLimitTier}` : ''}
            </Typography>
          )}
          {quality && <StatusPill tone={quality.tone}>{quality.label}</StatusPill>}
        </Stack>

        <Stack direction="row" gap={0.5} sx={{ mb: 1.5, borderBottom: `1px solid ${UI.line}` }} role="tablist">
          {TABS.map((t) => {
            const selected = t.key === tab;
            return (
              <Box
                key={t.key}
                component="button"
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setSearchParams({ tab: t.key }, { replace: true })}
                sx={{
                  fontFamily: UI.body,
                  fontSize: 13,
                  fontWeight: selected ? 600 : 500,
                  color: selected ? UI.ink : UI.muted,
                  bgcolor: 'transparent',
                  border: 'none',
                  borderBottom: `2px solid ${selected ? UI.gold : 'transparent'}`,
                  px: 1.25,
                  pt: 0.75,
                  pb: 1,
                  mb: '-1px',
                  cursor: 'pointer',
                  '&:hover': { color: UI.ink },
                  '&:focus-visible': { outline: `2px solid ${UI.gold}`, outlineOffset: -2 },
                }}
              >
                {t.label}
              </Box>
            );
          })}
        </Stack>

        <Box sx={{ mb: 2 }}>
          <HealthBanner health={health} loading={healthLoading} error={healthError} onCheck={checkHealth} />
        </Box>

        {tab === 'customer-flow' && <HubAutomationsTab templates={templates} mode="customerFlow" />}
        {tab === 'conversations' && <HubAutomationsTab templates={templates} mode="conversations" />}
        {tab === 'journeys' && <HubAutomationsTab templates={templates} mode="journeys" />}
        {tab === 'templates' && <HubTemplatesTab templates={templates} />}
        {tab === 'studio' && <WhatsappStudioTab />}
        {tab === 'contacts' && <HubContactsTab />}
        {tab === 'settings' && (
          <Box sx={{ maxWidth: 1000 }}>
            <WorkingHoursSection />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default WhatsappHubPage;
